import React from "react";
import { Vector3 } from "three";
import { folderUrl } from "../config";
import { ItemType } from "../interface";
import { ethers } from "ethers";

export const getFileUrl = (name: string): string => {
  return folderUrl + name;
};

export const getDemoUrl = (name: string): string => {
  return folderUrl + "demo_" + name + ".glb";
};

export const getVector = (data: number[]): Vector3 => {
  return new Vector3(data[0], data[1], data[2]);
};

export const formatDescription = (description: string) => {
  return description
    .replace(/\r\n/g, "\\n")
    .split("\\n")
    .map((line, index) => (
      <React.Fragment key={index}>
        {line}
        {index < line.length - 1 && <br />}
      </React.Fragment>
    ));
};

export const formatNumber = (number: number, isDollar = false): string => {
  if (number >= 1000000) {
    return (number / 1000000).toFixed(2) + "M";
  } else if (number >= 1000) {
    return (number / 1000).toFixed(2) + "K";
  } else {
    if (isDollar) {
      return number.toFixed(2);
    } else {
      return number.toString();
    }
  }
};

export const formatThousendNumber = (number: number) => {
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  }).format(number / 1000);
};

export const formatBalance = (
  value: bigint | undefined,
  decimals: number | undefined,
  precision = 4,
) => {
  // 将 BigInt 转为字符串再转换为 BigNumber
  if (!value || !decimals) return 0;

  const formatted = ethers.formatUnits(value.toString(), decimals);

  // 将字符串转换为浮点数并保留指定的小数位
  return parseFloat(formatted);
};

export const checkTrend = (item: ItemType): boolean => {
  if (item.average_price_lastday === 0) return true;
  return item.average_price_lastday >= item.average_price_sevenday;
};

export const getTrendProcent = (item: ItemType): string => {
  if (item.average_price_lastday === 0) return "";
  if (item.average_price_sevenday === 0)
    return (item.average_price_lastday * 100).toFixed(2) + "%";
  return (
    (
      ((item.average_price_lastday - item.average_price_sevenday) /
        item.average_price_sevenday) *
      100
    ).toFixed(2) + "%"
  );
};

export const formatCurrency = (
  price: number | string | undefined,
  currency: string = "USD",
  isInteger: boolean = false,
  keepTwoDigitals: boolean = false,
  rest?: any,
) => {
  if (!price) return "";
  if (typeof price === "string") price = Number(price);

  let digits = price < 5 ? 4 : 2;
  if (keepTwoDigitals) digits = 2;
  digits = isInteger ? 0 : digits;

  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
    ...rest,
  });

  return formatter.format(price);
};

export const formatIpfsUrl = (url: string) => {
  if (!url) return "";
  if (url.includes(".mp4")) return "";
  return url.startsWith("ipfs://")
    ? url.replace("ipfs://", "https://ipfs.io/ipfs/")
    : url;
};

export const delay = (ms: number) =>
  new Promise((resolve) => setTimeout(resolve, ms));

export const getExtractContractAddress = (str: string) => {
  const parts = str.split(".");
  return parts.length >= 3 ? parts[1].toLowerCase() : str;
};

export const formatContractAddress = (address: string) => {
  if (typeof address !== "string" || address.length < 12) {
    return address; // 如果地址太短，直接返回原地址
  }

  const start = address.slice(0, 7); // 前 7 位
  const end = address.slice(-5); // 后 5 位
  return `${start}...${end}`; // 拼接成格式化后的地址
};

export const getGrowthRate = (numbers: number[]) => {
  // 去掉所有 0
  const filteredNumbers = numbers.filter((num) => num !== 0);

  // 如果数组长度小于 2，返回 null 或提示信息
  if (filteredNumbers.length < 2) {
    return 0; // 或者可以返回一个默认值，如 0 或 'Not enough data'
  }

  // 获取第一个和最后一个元素
  const first = filteredNumbers[0];
  const last = filteredNumbers[filteredNumbers.length - 1];

  // 计算增长率
  const growthRate = ((last - first) / first) * 100; // 乘以 100 转换为百分比

  return growthRate; // 保留两位小数
};
